import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { api, formatErr, imgSrc } from "@/lib/api";

const fmt = (s) => new Date(s).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
const left = (s) => { const d = Math.ceil((new Date(s) - Date.now()) / 86400000); return d <= 1 ? "endet heute/morgen" : `noch ${d} Tage`; };

export function GameBlocksOverview() {
  const [list, setList] = useState(null);
  const [game, setGame] = useState("all");
  const load = useCallback(() => api.get("/admin/game-blocks/active").then(({ data }) => setList(data)).catch(() => setList([])), []);
  useEffect(() => { load(); }, [load]);
  const games = useMemo(() => [...new Map((list || []).map((r) => [r.game, r.game_name])).entries()], [list]);
  const unblock = async (r) => {
    if (!window.confirm(`„${r.game_name}“ für ${r.name} jetzt freigeben?`)) return;
    try { await api.put(`/admin/users/${r.user_id}/game-blocks/${r.game}`, { blocked: false }); toast.success(`„${r.game_name}“ für ${r.name} freigegeben`); load(); } catch (e) { toast.error(formatErr(e)); }
  };
  if (!list) return null;
  const shown = game === "all" ? list : list.filter((r) => r.game === game);
  return (
    <div className="bg-white rounded-3xl p-5 sm:p-6 border border-pink-100 space-y-4" data-testid="admin-gameblocks">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-bold text-lg flex items-center gap-2"><Lock className="w-5 h-5 text-red-500" /> Aktive Spiel-Sperren (<span data-testid="admin-gameblocks-count">{list.length}</span>)</h3>
        {games.length > 1 && (
          <select value={game} onChange={(e) => setGame(e.target.value)} data-testid="admin-gameblocks-filter" className="h-9 rounded-full border border-pink-200 px-3 text-sm bg-white">
            <option value="all">Alle Spiele</option>
            {games.map(([id, n]) => <option key={id} value={id}>{n}</option>)}
          </select>
        )}
      </div>
      <p className="text-sm text-slate-500">Neue Sperren setzt ihr unter „Alle Profile“ → „🔒 Spiele sperren“. Abgelaufene Sperren verschwinden automatisch.</p>
      {shown.length === 0 ? <p className="text-sm text-slate-400" data-testid="admin-gameblocks-empty">Aktuell ist niemand für ein Spiel gesperrt ✨</p> : (
        <ul className="space-y-2">
          {shown.map((r) => (
            <li key={`${r.user_id}-${r.game}`} className="rounded-2xl border border-red-100 bg-red-50/40 p-3 flex flex-wrap items-center gap-3" data-testid={`admin-gameblock-${r.user_id}-${r.game}`}>
              {r.picture ? <img src={imgSrc(r.picture)} alt="" className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center font-bold text-rose-600">{r.name?.[0]?.toUpperCase()}</div>}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold"><Link to={`/u/${r.user_id}`} className="hover:underline">{r.name}</Link> <span className="font-normal text-slate-400 text-xs">{r.email}</span></p>
                <p className="text-xs"><span className="font-bold text-red-600">{r.game_name}</span>{r.until ? ` · bis ${fmt(r.until)} (${left(r.until)})` : " · ohne Ablauf"}</p>
                {r.reason && <p className="text-xs text-slate-500 break-words">Grund: {r.reason}</p>}
              </div>
              <button onClick={() => unblock(r)} data-testid={`admin-gameblock-unblock-${r.user_id}-${r.game}`} className="px-4 h-9 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold">Freigeben</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
